import React, { useState, useEffect, useMemo } from 'react';
import {
  FiPlus,
  FiTrash2,
  FiInfo,
  FiSave,
  FiX,
  FiCpu,
  FiHelpCircle,
  FiExternalLink,
} from 'react-icons/fi';
import Modal from '../../../../components/ui/Modal';

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  bedrock: 'AWS Bedrock',
};

interface CriterionData {
  name: string;
  description: string;
  weight: number | string;
  scoring_guidance: string;
}

const DEFAULT_CRITERION: CriterionData = {
  name: '',
  description: '',
  weight: 1,
  scoring_guidance: '',
};

interface RubricTemplate {
  name: string;
  description: string;
  criteria: CriterionData[];
}

const RUBRIC_TEMPLATES: RubricTemplate[] = [
  {
    name: 'General Quality',
    description: 'General purpose evaluation for accuracy and relevance',
    criteria: [
      {
        name: 'Accuracy',
        description: 'Factual correctness of the response',
        weight: 1,
        scoring_guidance: 'Check for factual errors, misstatements, or incorrect information',
      },
      {
        name: 'Relevance',
        description: 'How well the response addresses the query',
        weight: 1,
        scoring_guidance: 'Evaluate if the response directly answers the question asked',
      },
      {
        name: 'Completeness',
        description: 'Whether all aspects are covered',
        weight: 0.8,
        scoring_guidance: 'Check if the response covers all parts of the question',
      },
    ],
  },
  {
    name: 'Code Review',
    description: 'Evaluation rubric for code generation tasks',
    criteria: [
      {
        name: 'Correctness',
        description: 'Code produces expected output',
        weight: 1.5,
        scoring_guidance: 'Does the code solve the stated problem correctly?',
      },
      {
        name: 'Code Quality',
        description: 'Clean, readable, maintainable code',
        weight: 1,
        scoring_guidance: 'Is the code well-structured and following best practices?',
      },
      {
        name: 'Efficiency',
        description: 'Optimal time/space complexity',
        weight: 0.8,
        scoring_guidance: 'Is the solution reasonably efficient?',
      },
      {
        name: 'Error Handling',
        description: 'Handles edge cases and errors',
        weight: 0.7,
        scoring_guidance: 'Does the code handle potential errors gracefully?',
      },
    ],
  },
  {
    name: 'Customer Support',
    description: 'Evaluation for customer service responses',
    criteria: [
      {
        name: 'Helpfulness',
        description: 'Response provides actionable assistance',
        weight: 1.2,
        scoring_guidance: 'Does the response help solve the customer issue?',
      },
      {
        name: 'Tone',
        description: 'Professional and empathetic communication',
        weight: 1,
        scoring_guidance: 'Is the tone appropriate for customer service?',
      },
      {
        name: 'Accuracy',
        description: 'Information provided is correct',
        weight: 1,
        scoring_guidance: 'Is the information factually accurate?',
      },
      {
        name: 'Clarity',
        description: 'Response is clear and easy to understand',
        weight: 0.8,
        scoring_guidance: 'Can a customer easily understand and follow the response?',
      },
    ],
  },
  {
    name: 'Content Writing',
    description: 'Evaluation for creative and marketing content',
    criteria: [
      {
        name: 'Engagement',
        description: 'Content is engaging and compelling',
        weight: 1.2,
        scoring_guidance: 'Would a reader find this content interesting?',
      },
      {
        name: 'Clarity',
        description: 'Message is clear and well-structured',
        weight: 1,
        scoring_guidance: 'Is the writing clear and logical?',
      },
      {
        name: 'Brand Voice',
        description: 'Matches expected tone and style',
        weight: 1,
        scoring_guidance: 'Does it match the brand guidelines?',
      },
      {
        name: 'Grammar',
        description: 'Proper grammar and spelling',
        weight: 0.6,
        scoring_guidance: 'Is it free of grammatical errors?',
      },
    ],
  },
];

export interface AvailableModel {
  id: string;
  name: string;
  provider: string;
}

export interface RubricCredential {
  provider: string;
}

export interface ExistingRubric {
  name?: string;
  description?: string;
  criteria?: CriterionData[];
  judge_provider?: string;
  judge_model_id?: string;
  is_active?: boolean;
}

interface FormData {
  name: string;
  description: string;
  criteria: CriterionData[];
  judge_provider: string;
  judge_model_id: string;
  is_active: boolean;
}

interface FormErrors {
  name?: string;
  criteria?: string;
  submit?: string;
  [key: string]: string | undefined;
}

interface RubricEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: object) => Promise<void>;
  rubric?: ExistingRubric | null;
  loading?: boolean;
  credentials?: RubricCredential[];
  availableModels?: AvailableModel[];
}

const RubricEditorModal: React.FC<RubricEditorModalProps> = ({
  isOpen,
  onClose,
  onSave,
  rubric = null,
  loading = false,
  credentials = [],
  availableModels = [],
}) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    criteria: [{ ...DEFAULT_CRITERION }],
    judge_provider: 'openai',
    judge_model_id: 'gpt-4o-mini',
    is_active: true,
  });

  const uniqueProviders = useMemo(() => {
    const providers = Array.from(new Set(credentials.map((c) => c.provider)));
    return providers.length > 0 ? providers : ['openai'];
  }, [credentials]);

  const modelsForProvider = useMemo(() => {
    const filtered = availableModels.filter((m) => m.provider === formData.judge_provider);
    return filtered.length > 0 ? filtered : [];
  }, [availableModels, formData.judge_provider]);

  useEffect(() => {
    if (modelsForProvider.length > 0) {
      const currentModelExists = modelsForProvider.some((m) => m.id === formData.judge_model_id);
      if (!currentModelExists) {
        setFormData((prev) => ({ ...prev, judge_model_id: modelsForProvider[0].id }));
      }
    }
  }, [modelsForProvider, formData.judge_model_id]);

  const canSave = credentials.length > 0 && modelsForProvider.length > 0;

  const [errors, setErrors] = useState<FormErrors>({});
  const [showTemplates, setShowTemplates] = useState(!rubric);

  useEffect(() => {
    if (rubric) {
      const hasProviderCred = uniqueProviders.includes(rubric.judge_provider ?? '');
      const provider = hasProviderCred
        ? (rubric.judge_provider ?? 'openai')
        : uniqueProviders[0] ?? 'openai';
      const providerModelsForEdit = availableModels.filter((m) => m.provider === provider);
      const modelExists = providerModelsForEdit.some((m) => m.id === rubric.judge_model_id);
      const model =
        hasProviderCred && modelExists
          ? (rubric.judge_model_id ?? '')
          : providerModelsForEdit[0]?.id ?? '';

      setFormData({
        name: rubric.name ?? '',
        description: rubric.description ?? '',
        criteria:
          (rubric.criteria?.length ?? 0) > 0
            ? rubric.criteria!.map((c) => ({ ...DEFAULT_CRITERION, ...c }))
            : [{ ...DEFAULT_CRITERION }],
        judge_provider: provider,
        judge_model_id: model,
        is_active: rubric.is_active !== false,
      });
      setShowTemplates(false);
    } else {
      const defaultProvider = uniqueProviders[0] ?? 'openai';
      const defaultModelsForProvider = availableModels.filter(
        (m) => m.provider === defaultProvider
      );
      setFormData({
        name: '',
        description: '',
        criteria: [{ ...DEFAULT_CRITERION }],
        judge_provider: defaultProvider,
        judge_model_id: defaultModelsForProvider[0]?.id ?? '',
        is_active: true,
      });
      setShowTemplates(true);
    }
    setErrors({});
  }, [rubric, isOpen, uniqueProviders, availableModels]);

  const applyTemplate = (template: RubricTemplate) => {
    setFormData({
      ...formData,
      name: template.name,
      description: template.description,
      criteria: template.criteria.map((c) => ({ ...c })),
    });
    setShowTemplates(false);
  };

  const addCriterion = () => {
    setFormData({
      ...formData,
      criteria: [...formData.criteria, { ...DEFAULT_CRITERION }],
    });
  };

  const removeCriterion = (index: number) => {
    if (formData.criteria.length <= 1) return;
    setFormData({
      ...formData,
      criteria: formData.criteria.filter((_, i) => i !== index),
    });
  };

  const updateCriterion = (index: number, field: keyof CriterionData, value: string | number) => {
    const newCriteria = [...formData.criteria];
    newCriteria[index] = { ...newCriteria[index], [field]: value };
    setFormData({ ...formData, criteria: newCriteria });
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (formData.criteria.length === 0) {
      newErrors.criteria = 'At least one criterion is required';
    }

    formData.criteria.forEach((criterion, index) => {
      if (!criterion.name.toString().trim()) {
        newErrors[`criterion_${index}_name`] = 'Criterion name is required';
      }
      const weight = Number.parseFloat(criterion.weight.toString());
      if (weight < 0 || weight > 10) {
        newErrors[`criterion_${index}_weight`] = 'Weight must be between 0 and 10';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      await onSave({
        name: formData.name.trim(),
        description: formData.description.trim(),
        criteria: formData.criteria.map((c) => ({
          name: c.name.toString().trim(),
          description: c.description.toString().trim(),
          weight: Number.parseFloat(c.weight.toString()) || 1,
          scoring_guidance: c.scoring_guidance.toString().trim(),
        })),
        judge_provider: formData.judge_provider,
        judge_model_id: formData.judge_model_id,
        is_active: formData.is_active,
      });
      onClose();
    } catch (err: unknown) {
      setErrors({ submit: err instanceof Error ? err.message : 'Save failed' });
    }
  };

  const totalWeight = formData.criteria.reduce(
    (sum, c) => sum + (Number.parseFloat(c.weight.toString()) || 0),
    0
  );

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Saving...';
  } else if (rubric) {
    submitButtonLabel = 'Update Rubric';
  } else {
    submitButtonLabel = 'Create Rubric';
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={rubric ? 'Edit Judge Rubric' : 'Create Judge Rubric'}
      maxWidth="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Template Selector */}
        {showTemplates && !rubric && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-3">
              <FiInfo className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <span className="font-medium text-blue-800 dark:text-blue-200">
                Start from a template
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {RUBRIC_TEMPLATES.map((template) => (
                <button
                  key={template.name}
                  type="button"
                  onClick={() => applyTemplate(template)}
                  className="p-3 text-left bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-500 dark:hover:border-blue-400 transition-colors"
                >
                  <div className="font-medium text-gray-900 dark:text-white">{template.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {template.description}
                  </div>
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => setShowTemplates(false)}
              className="mt-3 text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Or start from scratch
            </button>
          </div>
        )}

        {/* Basic Info */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label htmlFor="rubric-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rubric Name <span className="text-red-500">*</span>
            </label>
            <input
                                                                                  id="rubric-name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Code Review Quality"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white ${
                errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
          </div>

          <div>
            <label htmlFor="rubric-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
                                                                                  id="rubric-description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this rubric evaluates..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
            />
          </div>
        </div>

        {/* Judge Model Configuration */}
        <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
          <h4 className="font-medium text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <FiCpu className="w-4 h-4" />
            Judge Model
          </h4>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Select the LLM that will evaluate responses using this rubric
          </p>

          {credentials.length === 0 && (
            <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                No LLM providers configured. Go to Settings → LLM Providers to add credentials.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="rubric-provider" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Provider
              </label>
              <select
                                                                                  id="rubric-provider"
                value={formData.judge_provider}
                onChange={(e) => {
                  const provider = e.target.value;
                  const providerModels = availableModels.filter((m) => m.provider === provider);
                  setFormData({
                    ...formData,
                    judge_provider: provider,
                    judge_model_id: providerModels[0]?.id ?? '',
                  });
                }}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              >
                {uniqueProviders.map((provider) => (
                  <option key={provider} value={provider}>
                    {PROVIDER_LABELS[provider] ?? provider}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="rubric-model" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                Model
              </label>
              <select
                                                                                  id="rubric-model"
                value={formData.judge_model_id}
                onChange={(e) => setFormData({ ...formData, judge_model_id: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
              >
                {modelsForProvider.map((model) => (
                  <option key={model.id} value={model.id}>
                    {model.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {modelsForProvider.length === 0 && (
            <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-start gap-2">
                <FiHelpCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <p className="font-medium">No models found</p>
                  <p className="mt-1 text-xs opacity-80">
                    Check your API credentials or provider configuration in Settings.
                  </p>
                </div>
              </div>
            </div>
          )}

          {modelsForProvider.length > 0 && formData.judge_provider === 'bedrock' && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-start gap-2">
                <FiHelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-medium">Don't see your model?</p>
                  <ul className="mt-1 text-xs opacity-80 space-y-1 list-disc list-inside">
                    <li>
                      <strong>Claude 4.x:</strong> Add{' '}
                      <code className="bg-blue-100 dark:bg-blue-800/50 px-1 rounded">
                        bedrock:ListInferenceProfiles
                      </code>{' '}
                      to IAM policy
                    </li>
                    <li>
                      <strong>Other models:</strong> Enable ON_DEMAND in{' '}
                      <a
                        href="https://console.aws.amazon.com/bedrock/home#/modelaccess"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-0.5 underline hover:no-underline"
                      >
                        AWS Bedrock Console <FiExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Criteria Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h4 className="font-medium text-gray-900 dark:text-white">Evaluation Criteria</h4>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total weight: {totalWeight.toFixed(1)}
              </p>
            </div>
            <button
              type="button"
              onClick={addCriterion}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <FiPlus className="w-4 h-4" />
              Add Criterion
            </button>
          </div>

          {errors.criteria && (
            <p className="mb-2 text-sm text-red-500">{errors.criteria}</p>
          )}

          <div className="space-y-4">
            {formData.criteria.map((criterion, index) => (
              <div
                key={criterion.name || `criterion-${index}`}
                className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600"
              >
                <div className="flex items-start justify-between mb-3">
                  <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Criterion {index + 1}
                  </span>
                  {formData.criteria.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCriterion(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-4 gap-3">
                  <div className="col-span-2">
                    <label htmlFor="name" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input id="name"
                      type="text"
                      value={criterion.name}
                      onChange={(e) => updateCriterion(index, 'name', e.target.value)}
                      placeholder="e.g., Accuracy"
                      className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white ${
                        errors[`criterion_${index}_name`]
                          ? 'border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>

                  <div>
                    <label htmlFor="weight" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Weight
                    </label>
                    <input id="weight"
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      value={criterion.weight}
                      onChange={(e) => updateCriterion(index, 'weight', e.target.value)}
                      className={`w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white ${
                        errors[`criterion_${index}_weight`]
                          ? 'border-red-500'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}
                    />
                  </div>

                  <div>
                    <span className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Relative %
                    </span>
                    <div className="px-2 py-1.5 text-sm bg-gray-100 dark:bg-gray-600 rounded text-center text-gray-700 dark:text-gray-200">
                      {totalWeight > 0
                        ? (
                            (Number.parseFloat(criterion.weight.toString()) / totalWeight) *
                            100
                          ).toFixed(0)
                        : 0}
                      %
                    </div>
                  </div>

                  <div className="col-span-4">
                    <label htmlFor="rubric-description" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Description
                    </label>
                    <input
                                                                                  id="rubric-description"
                      type="text"
                      value={criterion.description}
                      onChange={(e) => updateCriterion(index, 'description', e.target.value)}
                      placeholder="What this criterion evaluates..."
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="col-span-4">
                    <label htmlFor="scoring-guidance" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Scoring Guidance
                    </label>
                    <textarea id="scoring-guidance"
                      value={criterion.scoring_guidance}
                      onChange={(e) => updateCriterion(index, 'scoring_guidance', e.target.value)}
                      placeholder="Instructions for how the judge should evaluate this criterion..."
                      rows={2}
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-900 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Active Toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="is_active"
            checked={formData.is_active}
            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
          />
          <label htmlFor="is_active" className="text-sm text-gray-700 dark:text-gray-300">
            Active (available for use in evaluations)
          </label>
        </div>

        {errors.submit && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{errors.submit}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            <FiX className="w-4 h-4 inline mr-1" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !canSave}
            title={canSave ? '' : 'Configure LLM providers in Settings first'}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiSave className="w-4 h-4 inline mr-1" />
            {submitButtonLabel}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RubricEditorModal;
